import { BRAND_SPRITES } from './brandSprites';
import { SOUND_EFFECTS, type SoundEffectId } from './creatorPacks';
import { machineArtForTable, THEME_ART_ATLAS_URL } from './themeMachineArt';
import { pinballAudio } from './proceduralAudio';
import { soundEffectsForPack } from './soundVariations';
import type { TableDefinition } from '../engine/tables/definition';

// Keep the table preload focused on assets gameplay/rendering can actually use.
// The compact generated machine-art atlas is only warmed for themed machines;
// Grand Circuit intentionally keeps the existing Pin presentation.
const FIXED_TABLE_ASSETS = [BRAND_SPRITES.pinHero] as const;

const IMAGE_EXTENSION = /\.(?:avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i;
const ASSET_URL = /^(?:https?:|data:|blob:|\/)/i;
const MAX_DECODED_IMAGES = 48;
const PRELOAD_TIMEOUT_MS = 3500;
const AUDIO_PRELOAD_TIMEOUT_MS = PRELOAD_TIMEOUT_MS;
const SOUND_EFFECT_IDS = new Set<string>(SOUND_EFFECTS.map((effect) => effect.id));
const preloadCache = new Map<string, Promise<void>>();
const decodedImageCache = new Map<string, HTMLImageElement>();

type OfflineAudioWindow = Window & typeof globalThis & {
  webkitOfflineAudioContext?: typeof OfflineAudioContext;
};

export type TablePreloadProgress = (progress: number) => void;

function looksLikeAssetUrl(value: string | undefined): value is string {
  return Boolean(value && ASSET_URL.test(value));
}

function tableAssetUrls(table: TableDefinition): string[] {
  const candidates = new Set<string>(FIXED_TABLE_ASSETS);
  if (machineArtForTable(table)) candidates.add(THEME_ART_ATLAS_URL);
  if (looksLikeAssetUrl(table.theme.playfieldArt)) candidates.add(table.theme.playfieldArt);
  if (looksLikeAssetUrl(table.theme.envMap)) candidates.add(table.theme.envMap);
  for (const value of Object.values(table.theme.artSlots ?? {})) {
    if (looksLikeAssetUrl(value)) candidates.add(value);
  }
  for (const part of table.parts) {
    if (looksLikeAssetUrl(part.artRef)) candidates.add(part.artRef);
  }
  return [...candidates];
}

export function tableAudioEffects(table: TableDefinition): SoundEffectId[] {
  const effects = new Set<SoundEffectId>(soundEffectsForPack(table.theme.soundPackId));
  for (const candidate of Object.values(table.theme.soundEventMap ?? {})) {
    if (candidate && SOUND_EFFECT_IDS.has(candidate)) effects.add(candidate as SoundEffectId);
  }
  for (const part of table.parts) {
    if (part.soundRef && SOUND_EFFECT_IDS.has(part.soundRef)) effects.add(part.soundRef as SoundEffectId);
  }
  return [...effects];
}

function retainDecodedImage(url: string, image: HTMLImageElement): void {
  decodedImageCache.delete(url);
  decodedImageCache.set(url, image);

  while (decodedImageCache.size > MAX_DECODED_IMAGES) {
    const oldestUrl = decodedImageCache.keys().next().value as string | undefined;
    if (!oldestUrl) break;
    decodedImageCache.delete(oldestUrl);
    preloadCache.delete(oldestUrl);
  }
}

function preloadImage(url: string): Promise<void> {
  if (typeof Image === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
    const cleanup = () => {
      if (timer !== undefined) globalThis.clearTimeout(timer);
      timer = undefined;
      image.onload = null;
      image.onerror = null;
    };
    const finish = (decoded: boolean) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (decoded) retainDecodedImage(url, image);
      else preloadCache.delete(url);
      resolve();
    };
    timer = globalThis.setTimeout(() => {
      image.src = '';
      finish(false);
    }, PRELOAD_TIMEOUT_MS);
    image.decoding = 'async';
    image.loading = 'eager';
    if ('fetchPriority' in image) image.fetchPriority = 'high';
    image.onload = () => {
      if (settled) return;
      const decoded = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
      void decoded
        .then(() => finish(true))
        .catch(() => finish(false));
    };
    image.onerror = () => finish(false);
    image.src = url;
  });
}

async function preloadBinary(url: string): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = globalThis.setTimeout(() => {
      controller?.abort();
      reject(new Error('Asset preload timed out.'));
    }, PRELOAD_TIMEOUT_MS);
  });

  try {
    const preload = (async () => {
      const response = await fetch(url, { cache: 'force-cache', signal: controller?.signal });
      if (!response.ok) throw new Error(`Asset preload failed with ${response.status}.`);
      await response.arrayBuffer();
    })();
    await Promise.race([preload, timeout]);
  } catch {
    preloadCache.delete(url);
  } finally {
    if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
  }
}

function preloadAsset(url: string): Promise<void> {
  const cached = preloadCache.get(url);
  if (cached) return cached;
  const pending = IMAGE_EXTENSION.test(url) ? preloadImage(url) : preloadBinary(url);
  preloadCache.set(url, pending);
  return pending;
}

function canPreRenderAudio(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.OfflineAudioContext
      ?? (window as OfflineAudioWindow).webkitOfflineAudioContext,
  );
}

async function preloadAudio(table: TableDefinition): Promise<void> {
  try {
    pinballAudio.prepare();
  } catch {
    // Audio is optional and can retry on a later user gesture.
  }

  if (!canPreRenderAudio()) return;

  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timeoutId = globalThis.setTimeout(resolve, AUDIO_PRELOAD_TIMEOUT_MS);
  });
  const warmup = pinballAudio.prepareEffects(
    tableAudioEffects(table),
    table.theme.soundPackId,
  ).catch(() => {
    // Runtime playback has a lightweight live-synthesis fallback.
  });

  try {
    await Promise.race([warmup, timeout]);
  } finally {
    if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
  }
}

function reportPreloadProgress(
  onProgress: TablePreloadProgress | undefined,
  progress: number,
): void {
  try {
    onProgress?.(progress);
  } catch {
    // Observational UI only; never turn progress reporting into a load failure.
  }
}

/**
 * Warm renderer assets and pre-render the selected procedural sound pack while
 * the table loading state is visible. Decoded images are retained so the
 * renderer can reuse warm browser resources. Audio warmup is best-effort and
 * bounded because runtime sound hits can fall back to live synthesis.
 */
export async function preloadTableExperience(
  table: TableDefinition,
  onProgress?: TablePreloadProgress,
): Promise<void> {
  const urls = tableAssetUrls(table);
  const total = Math.max(1, urls.length + 1);
  let completed = 0;
  const report = () => reportPreloadProgress(onProgress, Math.min(1, completed / total));
  const completeOne = () => {
    completed += 1;
    report();
  };

  reportPreloadProgress(onProgress, 0);

  const audioWarmup = preloadAudio(table).finally(completeOne);

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    await audioWarmup;
    reportPreloadProgress(onProgress, 1);
    return;
  }

  await Promise.all([
    audioWarmup,
    ...urls.map(async (url) => {
      try {
        await preloadAsset(url);
      } finally {
        completeOne();
      }
    }),
  ]);
  reportPreloadProgress(onProgress, 1);
}
